import "./Select.scss";

import cx from "classnames";
import Tooltip from "components/Tooltip/Tooltip";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import type {
  ClearIndicatorProps,
  MultiValueRemoveProps,
  Props as SelectPrimitiveProps,
} from "react-select";
import SelectPrimitive, { components as defaultComponents } from "react-select";

const DEFAULT_MAX_MENU_HEIGHT = 300;

/** Prefer opening upward when the menu's max height would exceed space below. */
export const resolveMenuPlacement = (
  element: HTMLElement,
  maxMenuHeight: number,
): "top" | "bottom" => {
  const control =
    element.querySelector<HTMLElement>('[class*="kaboodle__control"]') ??
    element;
  const { bottom, top } = control.getBoundingClientRect();
  const spaceBelow = window.innerHeight - bottom;
  const spaceAbove = top;

  if (spaceBelow >= maxMenuHeight) {
    return "bottom";
  }

  if (spaceAbove >= maxMenuHeight) {
    return "top";
  }

  return spaceAbove > spaceBelow ? "top" : "bottom";
};

export type OptionType = {
  label: string;
  value: string | number | boolean | null | undefined;
};

export type SelectProps = {
  name: string;
  label?: string;
  errors?: FieldErrors;
  readOnly?: boolean;
  classNames?: string;
  containerClassNames?: string;
  afterLabel?: React.ReactNode;
  isClearable?: boolean;
  components?: Partial<SelectPrimitiveProps<OptionType>["components"]>;
  isFocused?: boolean;
  valueContainerWidth?: string;
  widthVariant?: "medium" | "wide" | "full"; // width of the value container (overflowing names, etc)
  tooltip?: string;
  tooltipPlace?: "top" | "right" | "bottom" | "left";
} & SelectPrimitiveProps<OptionType | OptionType[]>;

const Clear = () => <span className="material-icons clear-icon">close</span>;

const ClearIndicator = ({
  children = <Clear />,
  // The next variable is needed for react-select to pass the third argument correctly
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  getStyles,
  innerProps: { ref, ...restInnerProps },
}: ClearIndicatorProps) => {
  return (
    <div {...restInnerProps} ref={ref}>
      {children}
    </div>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <defaultComponents.MultiValueRemove {...props}>
      <span className="material-icons multi-value-remove-icon">close</span>
    </defaultComponents.MultiValueRemove>
  );
};

const DropdownIndicator = () => {
  return (
    <div className="indicator">
      <span className="icon material-icons">keyboard_arrow_down</span>
    </div>
  );
};

const Select = ({
  name,
  label,
  errors = {},
  readOnly = false,
  classNames,
  containerClassNames,
  afterLabel,
  components = {},
  isClearable = true,
  isFocused,
  styles: customStyles,
  valueContainerWidth,
  widthVariant = "medium",
  tooltip,
  tooltipPlace = "top",
  ...rest
}: SelectProps) => {
  const {
    menuPlacement: menuPlacementProp,
    maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
    onMenuOpen: onMenuOpenProp,
    onMenuClose: onMenuCloseProp,
    options,
    isLoading,
    ...selectProps
  } = rest;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [computedMenuPlacement, setComputedMenuPlacement] = useState<
    "top" | "bottom"
  >("bottom");

  const useAutoMenuPlacement =
    menuPlacementProp === undefined || menuPlacementProp === "auto";

  const recalculateMenuPlacement = useCallback(() => {
    if (!wrapperRef.current) {
      return;
    }

    setComputedMenuPlacement(
      resolveMenuPlacement(wrapperRef.current, maxMenuHeight),
    );
  }, [maxMenuHeight]);

  const optionsLength = Array.isArray(options) ? options.length : 0;

  useLayoutEffect(() => {
    if (!menuIsOpen || !useAutoMenuPlacement) {
      return;
    }

    recalculateMenuPlacement();
  }, [
    menuIsOpen,
    optionsLength,
    isLoading,
    useAutoMenuPlacement,
    recalculateMenuPlacement,
  ]);

  const handleMenuOpen = useCallback(() => {
    if (useAutoMenuPlacement && wrapperRef.current) {
      setComputedMenuPlacement(
        resolveMenuPlacement(wrapperRef.current, maxMenuHeight),
      );
    }
    setMenuIsOpen(true);
    onMenuOpenProp?.();
  }, [useAutoMenuPlacement, maxMenuHeight, onMenuOpenProp]);

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false);
    onMenuCloseProp?.();
  }, [onMenuCloseProp]);

  const menuPlacement = useAutoMenuPlacement
    ? computedMenuPlacement
    : menuPlacementProp;

  const selectElement = (
    <div className={cx("Select", classNames)}>
      {label && <label>{label}</label>}
      {afterLabel}
      <div
        ref={wrapperRef}
        className={cx("select", containerClassNames, {
          [`select--${widthVariant}`]: widthVariant,
        })}
      >
        <SelectPrimitive
          {...selectProps}
          options={options}
          isLoading={isLoading}
          isClearable={isClearable}
          className={cx({
            "select--Error": errors && errors?.[name],
            "select--Disabled": readOnly,
            "select--Focused": isFocused,
          })}
          isDisabled={readOnly}
          classNamePrefix="kaboodle"
          components={{
            ClearIndicator,
            MultiValueRemove,
            DropdownIndicator,
            ...components,
          }}
          name={name || ""}
          hideSelectedOptions={true}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "509ed3",
            },
          })}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          maxMenuHeight={maxMenuHeight}
          menuPlacement={menuPlacement}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          styles={{
            ...customStyles,
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            menu: (base) => ({
              ...base,
              fontSize: "10px",
              zIndex: 9999,
            }),
            singleValue: (base) => ({
              ...base,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }),
            valueContainer: (base) => ({
              ...base,
              minWidth: 0,
              ...(valueContainerWidth ? { maxWidth: valueContainerWidth } : {}),
            }),
          }}
        />
      </div>
    </div>
  );

  /* conditionally wrap with Tooltip if a tooltip is provided */
  if (tooltip) {
    return (
      <Tooltip content={tooltip} place={tooltipPlace}>
        {selectElement}
      </Tooltip>
    );
  }

  return selectElement;
};

Select.displayName = "Select";

export default Select;
